import { Component, Input, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { IEquipmentTrackingModel, IStructureModel, IStructureTypeModel, StructureModel } from "@models";
import { EquipmentTrackingService, GlobalsService, StructureTypeService } from "@services";
import { SlickConfirmDialogComponent, SlickConfirmDialogResults } from "@slick-components";
import { EquipmentTrackingComponent } from "@shared-components/equipment-tracking/equipment-tracking.component";
import Swal from "sweetalert2";

// The "Unassigned" entry in the structure list. Not a real row: it collects everything recorded before
// assets existed, so the office can still find it (and, eventually, move it onto a real pool).
const UNASSIGNED_ID = 0;

type AssetTab = "orenda" | "readings" | "equipment";

@Component({
	selector: "assets",
	templateUrl: "./assets.component.html",
	styleUrls: ["./assets.component.scss"],
	providers: [EquipmentTrackingService]
})
export class AssetsComponent implements OnChanges {
	// Assets hang off the customer, so the same pools and spas show on every job at that site.
	@Input() customerId!: number;
	// Both optional: the tab also runs from the customer page, where there's no job behind it.
	@Input() jobId: number = 0;
	@Input() documentKey: string;

	@ViewChild("deleteStructureConfirmRef") deleteStructureConfirmRef: SlickConfirmDialogComponent;
	@ViewChild(EquipmentTrackingComponent) equipmentTrackingRef: EquipmentTrackingComponent;

	showOrenda: boolean = GlobalsService.company.useOrenda;
	showReadingsAndDosages: boolean = GlobalsService.company.useReadingsAndDosages;

	structureTypes: IStructureTypeModel[] = [];
	structures: IStructureModel[] = [];
	selectedStructure: IStructureModel = null;

	isLoading: boolean = false;
	isLoadingEquipment: boolean = false;
	selectedTab: AssetTab = null;

	// The equipment-tracking component is fed its list rather than loading its own, so the load has to
	// happen here - once per asset, when the tab is actually opened.
	equipmentTrackingModels: IEquipmentTrackingModel[] = [];

	// ---- add / edit structure form ----
	showStructureForm: boolean = false;
	addStructureTypeId: number = 0;
	structureDescription: string = "";
	poolShape: "rectangle" | "round" | "oval" = "rectangle";
	isCalculating: boolean = false;
	poolGallons: number = null;
	poolLength: number = null;
	poolWidth: number = null;
	poolDiameter: number = null;
	poolShallowDepth: number = null;
	poolDeepDepth: number = null;
	calculatedGallons: number = null;
	editingStructure: IStructureModel = null;

	readonly unassignedId: number = UNASSIGNED_ID;

	constructor(private readonly structureTypeService: StructureTypeService,
		private readonly equipmentTrackingService: EquipmentTrackingService) { }

	async ngOnChanges(changes: SimpleChanges) {
		// The job page keeps this alive across its tabs, so it can be handed another job of the same
		// customer, and the Unassigned bucket is per job
		if ((changes.customerId || changes.jobId) && this.customerId)
			await this.refresh();
	}

	// ---- unsaved Auto Cover changes ----

	hasUnsavedEquipment(): boolean {
		return this.equipmentTrackingRef?.hasUnsavedAutoCoverChanges() ?? false;
	}

	// For the host's own Save. False if the user backed out or the save failed.
	async saveUnsavedEquipment(): Promise<boolean> {
		return this.equipmentTrackingRef?.saveIfAutoCoverChanged() ?? true;
	}

	// Moving to another asset or tab reloads (or drops) the equipment list, so an Auto Cover sheet that
	// was filled in and never saved would quietly vanish. True when it's fine to go.
	private async canLeaveEquipment(): Promise<boolean> {
		if (!this.hasUnsavedEquipment())
			return true;

		const result = await Swal.fire({
			icon: 'warning',
			title: 'Unsaved Auto Cover changes',
			text: "The Auto Cover sheet has changes that haven't been saved.",
			showDenyButton: true,
			showCancelButton: true,
			confirmButtonText: 'Save',
			denyButtonText: 'Discard',
			cancelButtonText: 'Keep Editing',
			confirmButtonColor: '#007bff',
			heightAuto: false
		});

		if (result.isConfirmed)
			return this.saveUnsavedEquipment();

		return result.isDenied;
	}

	async onTabClicked(tab: AssetTab) {
		if (await this.canLeaveEquipment())
			await this.selectTab(tab);
	}

	async refresh(selectId: number = null) {
		this.isLoading = true;

		// Reloading after an add or an edit shouldn't move the user: they stay on the asset they were
		// looking at (Unassigned included) and on the tab they were reading, where both still exist.
		const previousTab = this.selectedTab;
		const previousId = (selectId !== null) ? selectId : (this.selectedStructure?.id ?? null);

		try {
			if (this.structureTypes.length === 0)
				this.structureTypes = await this.structureTypeService.getStructureTypes();

			this.structures = await this.structureTypeService.getStructures(this.customerId);

			this.selectedStructure = (previousId === UNASSIGNED_ID)
				? this.buildUnassigned()
				: (this.structures.find(x => x.id === previousId) ?? this.structures[0] ?? null);

			this.equipmentTrackingModels = [];

			await this.selectTab(this.isTabAvailable(previousTab) ? previousTab : this.firstAvailableTab());
		}
		finally {
			this.isLoading = false;
		}
	}

	async selectStructure(structure: IStructureModel) {
		if (!(await this.canLeaveEquipment()))
			return;

		this.selectedStructure = structure;
		this.equipmentTrackingModels = [];
		this.selectTab(this.isTabAvailable(this.selectedTab) ? this.selectedTab : this.firstAvailableTab());
	}

	async selectUnassigned() {
		if (!(await this.canLeaveEquipment()))
			return;

		this.selectedStructure = this.buildUnassigned();
		this.equipmentTrackingModels = [];
		this.selectTab(this.isTabAvailable(this.selectedTab) ? this.selectedTab : this.firstAvailableTab());
	}

	// Modelled as a structure with id 0 so everything downstream takes the same shape. The server reads
	// 0 as "whatever isn't attached to a live structure".
	private buildUnassigned(): IStructureModel {
		return new StructureModel({ id: UNASSIGNED_ID, customerId: this.customerId, description: "Unassigned" });
	}

	private isTabAvailable(tab: AssetTab): boolean {
		if (!tab || !this.selectedStructure)
			return false;

		if (tab === "orenda")
			return this.canShowOrenda();

		if (tab === "readings")
			return this.canShowReadingsAndDosages();

		return this.canShowEquipment();
	}

	async selectTab(tab: AssetTab) {
		this.selectedTab = tab;

		if (tab === "equipment")
			await this.loadEquipment();
	}

	private async loadEquipment() {
		this.isLoadingEquipment = true;

		try {
			this.equipmentTrackingModels =
				await this.equipmentTrackingService.getEquipmentTrackings(this.jobId, this.selectedStructure?.id ?? 0);
		}
		finally {
			this.isLoadingEquipment = false;
		}
	}

	get isUnassigned(): boolean {
		return this.selectedStructure?.id === UNASSIGNED_ID;
	}

	// Orenda needs a volume to dose against, so it's meaningless on the Unassigned bucket. Equipment is
	// stored per job as well as per structure, so on the customer page there's no job to fall back to.
	canShowOrenda(): boolean {
		return this.showOrenda && !this.isUnassigned;
	}

	canShowReadingsAndDosages(): boolean {
		return this.showReadingsAndDosages;
	}

	canShowEquipment(): boolean {
		return !this.isUnassigned || this.jobId > 0;
	}

	private firstAvailableTab(): AssetTab {
		if (!this.selectedStructure)
			return null;

		if (this.canShowOrenda())
			return "orenda";

		if (this.canShowReadingsAndDosages())
			return "readings";

		if (this.canShowEquipment())
			return "equipment";

		return null;
	}

	getStructureTypeName(structureTypeId: number): string {
		return this.structureTypes?.find(t => t.id === structureTypeId)?.name ?? "Structure";
	}

	get selectedStructureTitle(): string {
		if (!this.selectedStructure)
			return "";

		if (this.isUnassigned)
			return "Unassigned";

		return this.getStructureTypeName(this.selectedStructure.structureTypeId);
	}

	// ---- add ----

	toggleStructureForm() {
		this.showStructureForm = !this.showStructureForm;

		if (this.showStructureForm)
			this.resetStructureForm();
	}

	private resetStructureForm() {
		this.addStructureTypeId = 0;
		this.structureDescription = "";
		this.poolShape = "rectangle";
		this.isCalculating = false;
		this.poolGallons = null;
		this.poolLength = null;
		this.poolWidth = null;
		this.poolDiameter = null;
		this.poolShallowDepth = null;
		this.poolDeepDepth = null;
		this.calculatedGallons = null;
	}

	canSubmitStructure(): boolean {
		return !!this.structureDescription?.trim() && Number(this.addStructureTypeId) > 0;
	}

	async addStructure(): Promise<void> {
		if (!this.canSubmitStructure())
			return;

		// Adding the customer's first pool/spa moves their saved Auto Cover onto it, which would leave
		// unsaved edits to that sheet behind
		if (!(await this.canLeaveEquipment()))
			return;

		const structure = new StructureModel({
			id: 0,
			structureTypeId: Number(this.addStructureTypeId),
			// Records which job the asset was added from. Null on the customer page, where there is none.
			jobId: this.jobId > 0 ? this.jobId : null,
			customerId: this.customerId,
			description: this.structureDescription.trim(),
			volume: (this.isCalculating ? this.calculatedGallons : this.poolGallons) ?? 0,
			height: 0,
			width: 0,
			depth: 0
		});

		const newStructureId = await this.structureTypeService.addStructure(structure);

		this.showStructureForm = false;
		this.resetStructureForm();

		await this.refresh(newStructureId);
	}

	// ---- edit / delete ----

	editStructure(structure: IStructureModel) {
		this.editingStructure = { ...structure };
	}

	cancelStructureEdit() {
		this.editingStructure = null;
	}

	async saveStructureEdit(): Promise<void> {
		if (!this.editingStructure)
			return;

		if (!(await this.canLeaveEquipment()))
			return;

		const editedId = this.editingStructure.id;
		await this.structureTypeService.updateStructure(this.editingStructure);
		this.editingStructure = null;

		await this.refresh(editedId);
	}

	async deleteStructure(structure: IStructureModel): Promise<void> {
		if (!(await this.canLeaveEquipment()))
			return;

		const result = await this.deleteStructureConfirmRef.confirm();

		if (result !== SlickConfirmDialogResults.Ok)
			return;

		await this.structureTypeService.deleteStructure(structure);

		if (this.selectedStructure?.id === structure.id)
			this.selectedStructure = null;

		await this.refresh();
	}

	// ---- volume ----

	calculateGallons(): void {
		let gallons: number = null;

		const averageDepth =
			this.poolShallowDepth != null &&
				this.poolDeepDepth != null &&
				this.poolShallowDepth > 0 &&
				this.poolDeepDepth > 0
				? (this.poolShallowDepth + this.poolDeepDepth) / 2
				: null;

		if (this.poolShape === "rectangle") {
			if (this.poolLength > 0 && this.poolWidth > 0 && averageDepth != null)
				gallons = Math.round(this.poolLength * this.poolWidth * averageDepth * 7.48);
		}
		else if (this.poolShape === "round") {
			if (this.poolDiameter > 0 && averageDepth != null)
				gallons = Math.round(Math.PI * (this.poolDiameter / 2) * (this.poolDiameter / 2) * averageDepth * 7.48052);
		}
		else if (this.poolShape === "oval") {
			if (this.poolLength > 0 && this.poolWidth > 0 && averageDepth != null)
				gallons = Math.round(this.poolLength * this.poolWidth * averageDepth * 6.7);
		}

		this.calculatedGallons = gallons;
	}
}
